import { centsToFormattedDollars } from '@fruitful/fruitils';
import { useSetAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import {
    ArrowRightIcon,
    BodyMediumRegular,
    BodySmallRegular,
    Icon,
    XStack,
    YStack,
} from 'smoothie';

import {
    selectedIncomeSplitExecutionAtom,
    selectedIncomeSplitExecutionIndexAtom,
} from '../state/IncomeSplitAtoms';
import {
    type IncomeSplitExecution,
    type IncomeSplitOverviewHistory,
} from '../types/IncomeSplitOverviewTypes';

export const IncomeSplitExecutions = ({
    month,
    onPressExecution,
    onSelectExecution,
}: {
    month: IncomeSplitOverviewHistory;
    /** Called on every row press, before selection (e.g. for analytics). */
    onPressExecution?: (execution: IncomeSplitExecution) => void;
    /**
     * When provided, tapping a row calls this instead of mutating the global
     * selection atoms. Lets the list be reused outside the overview page (e.g.
     * the Sprout chat widget, which opens a sheet) without shared-state coupling.
     */
    onSelectExecution?: (
        execution: IncomeSplitExecution,
        executionIndex: number,
    ) => void;
}) => {
    const setSelectedIncomeSplitExecution = useSetAtom(
        selectedIncomeSplitExecutionAtom,
    );
    const setSelectedIncomeSplitExecutionIndex = useSetAtom(
        selectedIncomeSplitExecutionIndexAtom,
    );

    const [hasMeasured, setHasMeasured] = useState(false);
    const hasMeasuredRef = useRef(false);

    const prevMonthForExecutionsRef = useRef(month.month);
    const [executions, setExecutions] = useState<IncomeSplitExecution[]>(
        month.executions,
    );

    // height animation for executions container
    const executionsHeight = useSharedValue(0);

    // fade animation for executions
    const executionsOpacity = useSharedValue(1);

    // height animation when executions list is empty
    useEffect(() => {
        if (!hasMeasuredRef.current) return;

        if (month.executions.length === 0) {
            executionsHeight.value = withTiming(0, {
                duration: 250,
                easing: Easing.bezier(0.65, 0, 0.35, 1),
            });
        }
    }, [month.executions.length, month.month]);

    // executions fade animation when month changes
    useEffect(() => {
        const monthChanged = prevMonthForExecutionsRef.current !== month.month;

        if (monthChanged) {
            prevMonthForExecutionsRef.current = month.month;
            executionsOpacity.value = 0;

            setExecutions([...month.executions]);

            requestAnimationFrame(() => {
                executionsOpacity.value = withTiming(1, {
                    duration: 250,
                    easing: Easing.bezier(0.65, 0, 0.35, 1),
                });
            });
        }
    }, [month.month, month.executions]);

    const animatedExecutionsHeightStyle = useAnimatedStyle(() => {
        const height = executionsHeight.value;
        // Guard against NaN/undefined values on web
        // Check if height is a valid positive number
        if (
            !hasMeasured ||
            height === 0 ||
            typeof height !== 'number' ||
            height < 0 ||
            Number.isNaN(height)
        ) {
            return {};
        }
        return {
            height,
            overflow: 'hidden' as const,
        };
    }, [hasMeasured]);

    const animatedExecutionsStyle = useAnimatedStyle(() => {
        return {
            opacity: executionsOpacity.value,
        };
    });

    return (
        <Animated.View style={animatedExecutionsHeightStyle}>
            {executions.length > 0 && (
                <Animated.View style={animatedExecutionsStyle}>
                    <YStack
                        key={`executions-${month.month}`}
                        onLayout={(e: LayoutChangeEvent) => {
                            const measuredHeight = e.nativeEvent.layout.height;
                            // guard against NaN/undefined values on web
                            if (
                                measuredHeight === undefined ||
                                isNaN(measuredHeight) ||
                                measuredHeight < 0
                            ) {
                                return;
                            }
                            if (!hasMeasuredRef.current) {
                                // first measurement - set immediately without animation
                                executionsHeight.value = measuredHeight;
                                hasMeasuredRef.current = true;
                                setHasMeasured(true);
                            } else {
                                // subsequent measurements - animate to new height
                                executionsHeight.value = withTiming(
                                    measuredHeight,
                                    {
                                        duration: 300,
                                        easing: Easing.bezier(0.65, 0, 0.35, 1),
                                    },
                                );
                            }
                        }}
                    >
                        {executions
                            .slice()
                            .reverse()
                            .map((execution, index) => {
                                const { description, amount, date, id } =
                                    execution;
                                return (
                                    <XStack
                                        key={id}
                                        width="100%"
                                        alignItems="center"
                                        gap={12}
                                        padding={16}
                                        backgroundColor="rgba(255, 255, 255, 0.5)"
                                        borderWidth={1}
                                        borderColor="#FCFCFC"
                                        cursor="pointer"
                                        onPress={() => {
                                            const executionIndex =
                                                executions.length - 1 - index;
                                            onPressExecution?.(execution);
                                            if (onSelectExecution != null) {
                                                onSelectExecution(
                                                    execution,
                                                    executionIndex,
                                                );
                                                return;
                                            }
                                            setSelectedIncomeSplitExecution(
                                                execution,
                                            );
                                            setSelectedIncomeSplitExecutionIndex(
                                                executionIndex,
                                            );
                                        }}
                                    >
                                        <YStack gap={4} flex={1} flexShrink={1}>
                                            <BodyMediumRegular
                                                numberOfLines={1}
                                            >
                                                {description}
                                            </BodyMediumRegular>
                                            <BodySmallRegular color="$gray1000">
                                                {centsToFormattedDollars(
                                                    amount,
                                                )}{' '}
                                                • {date}
                                            </BodySmallRegular>
                                        </YStack>
                                        <Icon icon={ArrowRightIcon} size={24} />
                                    </XStack>
                                );
                            })}
                    </YStack>
                </Animated.View>
            )}
        </Animated.View>
    );
};
